<script setup>
import { computed, onMounted, ref } from 'vue'
import { useCamerasStore } from '@/store/cameras'

const props = defineProps({
  readOnlySelection: { type: Boolean, default: true },
  availableCameras: { type: Array, default: () => null },
  multiple: { type: Boolean, default: true },
  includeDecommissionedCameras: { type: Boolean, default: false },
  preSelectFirstAvailableCamera: { type: Boolean, default: false },
})
const selectedCameras = defineModel('selectedCameras', { type: [Array, Object, String, null], default: () => [] })

const camerasStore = useCamerasStore()

const amountOfDisplayedCameras = ref(4)
const showHiddenCameras = ref(false)

const loading = computed(() => !availableCameras.value || availableCameras.value.length === 0)

const availableCameras = computed(() => {
  if (props.availableCameras) return props.availableCameras
  const cameras = props.includeDecommissionedCameras ? camerasStore.getCameras() : camerasStore.getNonDecommissionedCameras()
  return [...cameras].sort((a, b) => a.name.localeCompare(b.name))
})

const displayedSelectedCameras = computed(() => {
  if (!selectedCameras.value) return []
  if (!Array.isArray(selectedCameras.value)) {
    return [selectedCameras.value]
  }
  if (selectedCameras.value.length > amountOfDisplayedCameras.value) {
    return selectedCameras.value.slice(0, amountOfDisplayedCameras.value)
  }
  return selectedCameras.value
})

const hiddenCount = computed(() => {
  if (selectedCameras.value.length > amountOfDisplayedCameras.value) {
    return selectedCameras.value.length - amountOfDisplayedCameras.value
  }
  return 0
})

const unselectHiddenCameras = () => {
  selectedCameras.value = selectedCameras.value.filter(camera =>
    displayedSelectedCameras.value.map(camera => camera.cameraId).includes(camera.cameraId)
  )
}

onMounted(async () => {
  await camerasStore.ensureCamerasLoaded()

  // 1. case: handle single cameraId string and convert to array of single camera object
  if (typeof selectedCameras.value === 'string') {
    selectedCameras.value = [availableCameras.value.find(camera => camera.cameraId === selectedCameras.value)]
    return
  }

  // 2. case: handle single camera object and convert to array of single camera object
  const isObject = selectedCameras.value && typeof selectedCameras.value === 'object' && !Array.isArray(selectedCameras.value)
  if (isObject) {
    selectedCameras.value = [availableCameras.value.find(camera => camera.cameraId === selectedCameras.value.cameraId)]
  }

  // 3. case: handle array of cameraId strings and convert to array of camera objects
  if (Array.isArray(selectedCameras.value) && selectedCameras.value.length > 0) {
    const validSelectedCameras = availableCameras.value.filter(camera =>
      selectedCameras.value.some(selected => (typeof selected === 'string' ? selected === camera.cameraId : selected.cameraId === camera.cameraId))
    )
    selectedCameras.value = props.multiple ? validSelectedCameras : validSelectedCameras[0] || null
  }

  if (!props.preSelectFirstAvailableCamera) return
  if ((!selectedCameras.value || selectedCameras.value.length === 0) && availableCameras.value?.length > 0) {
    selectedCameras.value = availableCameras.value[0]
  }
})
</script>

<template>
  <v-autocomplete
    v-model="selectedCameras"
    style="min-width: 250px"
    :loading="loading"
    :auto-select-first="true"
    prepend-icon="mdi-cctv"
    :label="$t('general_interface.camera_picker.label')"
    variant="outlined"
    :items="availableCameras"
    item-title="name"
    :item-value="item => item"
    :multiple="props.multiple"
    chips
    :closable-chips="props.multiple"
    :readonly="props.readOnlySelection"
    :disabled="props.readOnlySelection"
    :clearable="!props.readOnlySelection && props.multiple"
    width="100%"
    hide-details
  >
    <!-- custom append inner slot for select all -->
    <template
      v-if="props.multiple && !props.readOnlySelection"
      #append-inner="{ blur }"
    >
      <div>
        <v-icon
          icon="mdi-expand-all"
          @click.stop="
            () => {
              blur()
              selectedCameras = availableCameras.map(camera => camera)
              showHiddenCameras = false
            }
          "
        />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ $t('camera_privacy.select_all') }}
        </v-tooltip>
      </div>
    </template>

    <!-- custom display of selected chips -->
    <template #chip="{ props: chipProps, item, index }">
      <!-- show only first 4 selected cameras -->
      <v-chip
        v-if="!showHiddenCameras && displayedSelectedCameras && displayedSelectedCameras.find(camera => camera.cameraId === item.value.cameraId)"
        v-bind="chipProps"
        :key="item.cameraId"
        :value="item.cameraId"
        :text="item.title"
      />

      <!-- hide remaining & sum up cameras -->
      <v-chip
        v-if="!showHiddenCameras && index === amountOfDisplayedCameras && hiddenCount > 0"
        v-bind="chipProps"
        prepend-icon="mdi-eye"
        color="grey"
        @click.stop="showHiddenCameras = true"
        @click:close="unselectHiddenCameras"
      >
        {{ $t('camera_privacy.more_cameras', { count: hiddenCount }) }}
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ $t('camera_privacy.show_all') }}
        </v-tooltip>
      </v-chip>

      <!-- show all cameras -->
      <v-chip
        v-if="showHiddenCameras"
        v-bind="chipProps"
        :key="item.cameraId"
        :value="item.cameraId"
        :text="item.title"
      />
    </template>
  </v-autocomplete>
</template>
